import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Inline Google Places city autocomplete — minimal styling for use inside
 * segmented search bars (Packages / One-Way / Outstation). Renders only the
 * <input> and a dropdown; no icons, no labels, no help text. The parent owns
 * the visual chrome.
 *
 * Props:
 *   value             current text value
 *   onChange(text)    fires on every keystroke + when a place is selected (with city name)
 *   onCitySelect(loc) fires when a place is chosen — gives full {city, lat, lng, state, ...}
 *   placeholder       input placeholder
 *   inputClassName    classes for the <input> (so it matches segment styling)
 *   onKeyDown         pass-through (e.g. Enter triggers Search)
 *   country           Google country restriction (default 'in')
 */

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEBOUNCE_MS = 500;

let scriptLoaded = false;
let scriptLoading = false;
const cbs = [];

function loadScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) { scriptLoaded = true; resolve(); return; }
    if (scriptLoading) { cbs.push({ resolve, reject }); return; }
    if (!GOOGLE_MAPS_API_KEY) { reject(new Error('Google Maps API key missing')); return; }
    scriptLoading = true;
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      resolve();
      cbs.forEach(c => c.resolve());
      cbs.length = 0;
    };
    s.onerror = (e) => {
      scriptLoading = false;
      reject(e);
      cbs.forEach(c => c.reject(e));
      cbs.length = 0;
    };
    document.head.appendChild(s);
  });
}

export default function CityAutocompleteInline({
  value = '',
  onChange,
  onCitySelect,
  placeholder = 'City',
  inputClassName = '',
  onKeyDown,
  country = 'in',
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceTimer = useRef(null);
  const wrapperRef = useRef(null);

  // Load Google Maps script
  useEffect(() => {
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled) return;
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      const div = document.createElement('div');
      placesService.current = new window.google.maps.places.PlacesService(div);
      setIsReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((query) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!query || query.length < 3 || !autocompleteService.current) {
      setSuggestions([]);
      setShowDropdown(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceTimer.current = setTimeout(() => {
      autocompleteService.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country },
          types: ['(cities)'],
        },
        (predictions, status) => {
          setLoading(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
            setShowDropdown(true);
          } else {
            setSuggestions([]);
            setShowDropdown(false);
          }
        }
      );
    }, DEBOUNCE_MS);
  }, [country]);

  const handleSelect = useCallback((prediction) => {
    if (!placesService.current) return;
    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_component', 'formatted_address', 'geometry', 'name'],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          // Fallback: use prediction text
          const city = prediction.structured_formatting?.main_text || prediction.description || '';
          setShowDropdown(false);
          onChange?.(city);
          onCitySelect?.({ city, address: prediction.description, lat: null, lng: null });
          return;
        }
        let city = '';
        let state = '';
        let countryName = '';
        let pincode = '';
        (place.address_components || []).forEach(comp => {
          const types = comp.types;
          if (types.includes('locality')) city = comp.long_name;
          else if (types.includes('administrative_area_level_2') && !city) city = comp.long_name;
          else if (types.includes('administrative_area_level_1')) state = comp.long_name;
          else if (types.includes('country')) countryName = comp.long_name;
          else if (types.includes('postal_code')) pincode = comp.long_name;
        });
        if (!city) city = place.name || prediction.structured_formatting?.main_text || '';
        const locationData = {
          address: place.formatted_address || prediction.description,
          name: place.name || '',
          city,
          state,
          country: countryName,
          pincode,
          lat: place.geometry?.location?.lat?.() ?? null,
          lng: place.geometry?.location?.lng?.() ?? null,
        };
        setShowDropdown(false);
        onChange?.(locationData.city);
        onCitySelect?.(locationData);
      }
    );
  }, [onChange, onCitySelect]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    search(v);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        placeholder={placeholder}
        disabled={disabled || !isReady}
        autoComplete="off"
        className={inputClassName}
      />
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-[9999] mt-1 left-0 right-0 min-w-[260px] bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(s)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0 flex items-start gap-2 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm truncate">
                  {s.structured_formatting?.main_text || s.description}
                </div>
                {s.structured_formatting?.secondary_text && (
                  <div className="text-gray-500 text-xs truncate">
                    {s.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            </button>
          ))}
          <div className="px-3 py-1 text-[9px] text-gray-400 text-right border-t border-gray-100">
            Powered by Google
          </div>
        </div>
      )}
      {loading && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}
